import { useEffect, createContext, type PropsWithChildren, useCallback, useState } from 'react';

import axios from 'axios';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import { GET_USER_DATA } from '../../config/apiPaths';
import { getUrls, processApiPath } from '../../utils/url';

export interface SupportProviderProps {
    supportId?: string;
    supportUuid?: string;
    onBack?: () => void;
    narrowStyle?: boolean;
    fullScreen?: boolean;
}

interface SupportContextInterface {
    supportId: string | undefined;
    loading: boolean;
    isNarrowStyle: boolean;
    isFullScreen: boolean;
    errorMessage: string;
    errorModalMessage: string;
    setErrorModalMessage: (message: string) => void;
    openErrorModal: boolean;
    setOpenErrorModal: (openEditModal: boolean) => void;
    supportData: any;
    setSupportData: (supportData: any) => void;
    onBack?: () => void;
    me: any;
    meFetched: boolean;
}

const SupportContext = createContext(undefined as unknown as SupportContextInterface);

const { Provider, Consumer } = SupportContext;

const SupportProvider = (props: PropsWithChildren<SupportProviderProps>) => {
    const search = new URLSearchParams(window.location.search);
    const supportUuid = search.get('support_uuid') || window.localStorage.getItem('support_uuid');

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);
    const { fullScreen, narrowStyle, supportId, children } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [supportData, setSupportData] = useState<any>({ loading: true, supportData: {} });
    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [me, setMe] = useState<any>({});
    const [meFetched, setMeFetched] = useState<boolean>(false);
    const { api } = getUrls();

    console.log('supportProvider supportId', supportId);

    const fetchSupportData = useCallback(async () => {
        try {
            // should get extra data not base data
            const { data } = await axios.get(
                `${api}${GET_USER_DATA}?support_id=${supportId}&support_uuid=${supportUuid}&auth_mode=${authMode}&demo_content=${showDemoContent}`
            );
            setSupportData(data);
            setIsLoading(false);
        } catch (err) {
            setErrorMessage(err!.toString());
            setSupportData(undefined);
            setIsLoading(false);
        }
    }, [api, supportId, authMode, supportUuid, showDemoContent]);

    useEffect(() => {
        if (!supportUuid || !supportId) {
            return;
        }

        const fetchMe = async () => {
            const getMeResponse = await axios.get(
                processApiPath(GET_USER_DATA, undefined, {
                    support_uuid: supportUuid,
                    support_id: supportId,
                    auth_mode: authMode
                })
            );
            setMe(getMeResponse);
            setMeFetched(true);
            fetchSupportData();
        };

        fetchMe();
    }, [api, supportId, fetchSupportData, authMode, supportUuid]);

    return (
        <Provider
            value={{
                supportId,
                loading: supportData.loading || isLoading,
                isFullScreen: !!fullScreen,
                isNarrowStyle: !!narrowStyle,
                openErrorModal,
                setOpenErrorModal,
                errorModalMessage,
                setErrorModalMessage,
                supportData,
                setSupportData,
                me,
                meFetched,
                errorMessage
            }}
        >
            {children}
        </Provider>
    );
};

export { SupportContext, SupportProvider, Consumer as SupportConsumer };
